/**
 * Shared Syncfusion grid toolbar helpers for individual-report table viewers.
 *
 * - **Single rAF**: Toolbar `enableItems` is deferred one frame so `toolbarModule` exists after `dataBound` and
 *   selection state matches what Syncfusion has applied (avoids Delete staying default-enabled on open).
 * - **Delete tooltip attachment**: Consumers pass `onPointerDownCapture` to snapshot selection before the toolbar
 *   click clears it (see column/row delete confirm flows).
 */
import type { GridComponent } from '@syncfusion/ej2-react-grids';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';

/** Holds one rAF id so it can be cancelled on unmount or when coalescing a new flush. */
export type ToolbarFlushRafHandle = { id: number | null };

export function createInitialToolbarFlushRafHandle(): ToolbarFlushRafHandle {
    return { id: null };
}

export function cancelScheduledToolbarFlushRaf(handleRef: MutableRefObject<ToolbarFlushRafHandle>): void {
    const id = handleRef.current.id;
    if (id != null) {
        cancelAnimationFrame(id);
    }
    handleRef.current.id = null;
}

/**
 * Runs `callback` on the next animation frame; coalesces rapid calls and cancels any in-flight frame first.
 */
export function scheduleToolbarFlushRaf(handleRef: MutableRefObject<ToolbarFlushRafHandle>, callback: () => void): void {
    cancelScheduledToolbarFlushRaf(handleRef);
    handleRef.current.id = requestAnimationFrame(() => {
        handleRef.current.id = null;
        callback();
    });
}

export type SyncfusionToolbarModule = {
    enableItems: (ids: string[], enabled: boolean) => void;
};

export function getSyncfusionToolbarModule(grid: GridComponent | null | undefined): SyncfusionToolbarModule | null {
    const toolbarModule = (grid as { toolbarModule?: SyncfusionToolbarModule } | null | undefined)?.toolbarModule;
    return toolbarModule?.enableItems ? toolbarModule : null;
}

export function syncfusionDeleteToolbarItemId(gridElementId: string): string {
    return `${gridElementId}_delete`;
}

export interface IToolbarFluentTooltipState {
    text: string | null;
    target: HTMLElement | null;
}

/**
 * Wires Fluent tooltip + pointerdown snapshot on the Syncfusion grid Delete toolbar item.
 * `attachedMarkerAttr` should be a data-attribute name; value `1` means listeners are already attached.
 */
export function attachSyncfusionDeleteToolbarFluentTooltip(params: {
    grid: GridComponent;
    attachedMarkerAttr: string;
    getTooltipText: () => string;
    setTooltipState: Dispatch<SetStateAction<IToolbarFluentTooltipState>>;
    onPointerDownCapture: () => void;
}): void {
    const { grid, attachedMarkerAttr, getTooltipText, setTooltipState, onPointerDownCapture } = params;
    const gridEl = grid.element;
    if (!gridEl?.id) {
        return;
    }
    const deleteBtn = gridEl.querySelector<HTMLElement>(`#${syncfusionDeleteToolbarItemId(gridEl.id)}`);
    if (!deleteBtn || deleteBtn.getAttribute(attachedMarkerAttr) === '1') {
        return;
    }
    deleteBtn.setAttribute(attachedMarkerAttr, '1');
    const show = () => setTooltipState({ text: getTooltipText(), target: deleteBtn });
    const hide = () => {
        setTooltipState((prev) => (prev.target === deleteBtn ? { text: null, target: null } : prev));
    };
    deleteBtn.addEventListener('mouseenter', show);
    deleteBtn.addEventListener('mouseleave', hide);
    deleteBtn.addEventListener('focus', show);
    deleteBtn.addEventListener('blur', hide);
    deleteBtn.addEventListener('pointerdown', onPointerDownCapture);
}
